// `enrollments`: Swift `AutoplayEnrollment` (GroundTeam/Models/Automation.swift): one record's progress through an
// autoplay. Decoding template `AutoplayEnrollment(autoplayId: UUID(), recordId: UUID())`
// (GroundTeam/Sync/ResilientDecoding.swift:124): a payload missing `autoplayId` or `recordId` decodes on the phone with
// a random UUID there (pointing at nothing), so it is not an error; mirrored with a fresh UUID. The web edits by
// patching the raw payload, so such a stand-in never reaches the wire unless the user sets that key.

import { ENROLLMENT_STATUSES, type EnrollmentStatus } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  DATE,
  INT,
  UUID,
  arrayOf,
  createModel,
  decodeModel,
  enumOf,
  field,
  nowWire,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface EnrollmentPayload extends Passthrough {
  id: string;
  autoplayId: string;
  recordId: string;
  /** Int. */
  currentStepIndex: number;
  nextDueAt: string;
  status: EnrollmentStatus;
  enrolledAt: string;
  enrolledBy?: string;
  completedStepIds: string[];
}

export const ENROLLMENT_KEYS = [
  'id',
  'autoplayId',
  'recordId',
  'currentStepIndex',
  'nextDueAt',
  'status',
  'enrolledAt',
  'enrolledBy',
  'completedStepIds',
] as const satisfies readonly (keyof Known<EnrollmentPayload>)[];

export const ENROLLMENT_FIELDS: FieldMap<EnrollmentPayload> = {
  id: field.strict(UUID),
  autoplayId: field.req(UUID, newId),
  recordId: field.req(UUID, newId),
  currentStepIndex: field.req(INT, () => 0),
  nextDueAt: field.req(DATE, nowWire),
  status: field.req(enumOf('EnrollmentStatus', ENROLLMENT_STATUSES), () => 'active'),
  enrolledAt: field.req(DATE, nowWire),
  enrolledBy: field.opt(UUID),
  completedStepIds: field.req(arrayOf(UUID), () => []),
};

export function decodeEnrollment(raw: JsonObject, envelopeId?: string): Decoded<EnrollmentPayload> {
  return decodeModel(ENROLLMENT_FIELDS, raw, {}, envelopeId);
}

/** Swift's memberwise init needs both ids. */
export type EnrollmentInit = Partial<Known<EnrollmentPayload>> & { autoplayId: string; recordId: string };

export function newEnrollment(init: EnrollmentInit): EnrollmentPayload {
  const now = nowWire();
  const defaults = {
    id: newId(),
    currentStepIndex: 0,
    nextDueAt: now,
    status: 'active',
    enrolledAt: now,
    completedStepIds: [],
  };
  return createModel(ENROLLMENT_FIELDS, defaults, init, 'enrollments');
}
